import React, { useState, useContext, useEffect } from "react";
import OrderStatusStepper from "./OrderStatusStepper";
import { UserOrdersContext } from '../../context/UserOrdersContext';
import axios from "axios";
import API_BASE_URL from "../../config/api";



const getStepIndex = (status) => {
  const normalized = (status || '').toLowerCase();
  if (['pending', 'order placed'].includes(normalized)) return 0;
  if (['paid', 'payment confirmed'].includes(normalized)) return 1;
  if (['processing', 'confirmed'].includes(normalized)) return 2;
  if (['shipped'].includes(normalized)) return 3;
  if (['out for delivery', 'out_for_delivery'].includes(normalized)) return 4;
  if (['delivered'].includes(normalized)) return 5;
  if (['cancelled', 'failed'].includes(normalized)) return 0;
  return 0;
};

export default function TrackOrder() {
  const { orders, loading, error, setOrders, setLoading, setError } = useContext(UserOrdersContext);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem('authToken');
    if (!token || (orders && orders.length > 0)) return;

    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${API_BASE_URL}/api/v1/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!mounted) return;

        if (res.ok && Array.isArray(data.orders)) {
          setOrders(data.orders);
        } else if (res.status === 401) {
          localStorage.removeItem('authToken');
          setOrders([]);
          setError('Please login again to see your orders.');
        } else {
          setOrders([]);
          setError(data.message || 'Unable to fetch orders');
        }
      } catch (e) {
        if (mounted) {
          setOrders([]);
          setError('Unable to fetch orders');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchOrders();
    return () => {
      mounted = false;
    };
  }, [orders, setOrders, setLoading, setError]);

  // Filter orders by search (order number or product name)
  const filteredOrders = orders.filter(order => {
    if (!search) return true;
    const orderNum = (order.orderNumber || "").toLowerCase();
    const productNames = (order.items || []).map(i => (i.name || "").toLowerCase()).join(" ");
    return orderNum.includes(search.toLowerCase()) || productNames.includes(search.toLowerCase());
  });

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setLocalError("");
  };

  const handleTrackByNumber = async () => {
    setLocalError("");
    setSelectedOrder(null);
    // fallback to public API if not found in user's orders
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/public/orders/${search}`);
      if (data && data.success && data.order) {
        setSelectedOrder(data.order);
      } else {
        setLocalError("Order not found.");
      }
    } catch (e) {
      setLocalError("Order not found or server error.");
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h2 className="text-4xl font-bold text-center mb-8">Track Your Orders</h2>
      <div className="bg-white p-6 rounded-xl shadow max-w-md mx-auto mb-8">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by Order Number or Product Name"
          className="w-full border rounded-lg px-3 py-2 mb-4"
        />
        <button
          onClick={handleTrackByNumber}
          className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold mb-2"
          disabled={loading || !search}
        >
          {loading ? "Searching..." : "Track by Order Number"}
        </button>
        {(error || localError) && <p className="text-red-600 mt-2">{error || localError}</p>}
      </div>

      {/* Order History List */}
      <div className="bg-white rounded-xl shadow-lg p-6 max-w-3xl mx-auto mb-8">
        <h3 className="text-2xl font-bold mb-4">Order History</h3>
        {orders.length === 0 && <div className="text-gray-500">No orders found.</div>}
        {filteredOrders.length === 0 && orders.length > 0 && <div className="text-gray-500">No orders match your search.</div>}
        <ul className="divide-y divide-gray-200">
          {filteredOrders.map(order => (
            <li key={order._id || order.orderNumber} className="py-4 flex flex-col md:flex-row md:items-center md:justify-between cursor-pointer hover:bg-blue-50 rounded-lg px-2 transition"
                onClick={() => handleSelectOrder(order)}>
              <div>
                <div className="font-semibold text-gray-800">Order #{order.orderNumber}</div>
                <div className="text-gray-600 text-sm">Placed: {order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}</div>
                <div className="text-gray-600 text-sm">Status: <span className="font-semibold text-blue-700">{order.status}</span></div>
              </div>
              <div className="text-right mt-2 md:mt-0">
                <div className="text-lg font-bold text-blue-700">₹{(order.totalAmount/100).toFixed(2)}</div>
                <div className="text-gray-600 text-sm">{order.currency || 'INR'}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Selected Order Details */}
      {selectedOrder && (
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
          <button className="mb-4 text-blue-600 underline" onClick={() => setSelectedOrder(null)}>&larr; Back to Order History</button>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4">
            <div>
              <div className="text-lg font-bold text-gray-800">Order #{selectedOrder.orderNumber}</div>
              <div className="text-gray-600 text-sm">Placed: {selectedOrder.createdAt ? new Date(selectedOrder.createdAt).toLocaleString() : '-'}</div>
              <div className="text-gray-600 text-sm">Status: <span className="font-semibold text-blue-700">{selectedOrder.status}</span></div>
              {selectedOrder.customer && (
                <div className="text-gray-600 text-sm mt-1">
                  {selectedOrder.customer.name} {selectedOrder.customer.phone ? ` • ${selectedOrder.customer.phone}` : ''}
                </div>
              )}
            </div>
            <div className="text-right mt-4 md:mt-0">
              <div className="text-xl font-bold text-blue-700">₹{(selectedOrder.totalAmount/100).toFixed(2)}</div>
              <div className="text-gray-600 text-sm">{selectedOrder.currency || 'INR'}</div>
            </div>
          </div>
          <div className="mb-4">
            <div className="font-semibold text-gray-700 mb-2">Items:</div>
            <ul className="divide-y divide-gray-200">
              {(selectedOrder.items || []).map((item, idx) => (
                <li key={idx} className="py-2 flex items-center">
                  <span className="font-medium text-gray-800">{item.name || item.productId}</span>
                  {item.quantity && <span className="ml-2 text-gray-600">x{item.quantity}</span>}
                  {item.price != null && <span className="ml-4 text-blue-700 font-semibold">₹{(item.price/100).toFixed(2)}</span>}
                </li>
              ))}
            </ul>
          </div>
          <div className="mb-4">
            <div className="font-semibold text-gray-700 mb-2">Shipping Details:</div>
            {selectedOrder.shippingAddress ? (
              <div className="text-gray-600 text-sm space-y-1">
                <div>{selectedOrder.shippingAddress.line1}</div>
                {selectedOrder.shippingAddress.line2 && <div>{selectedOrder.shippingAddress.line2}</div>}
                <div>{[selectedOrder.shippingAddress.city, selectedOrder.shippingAddress.state].filter(Boolean).join(', ')}</div>
                <div>{selectedOrder.shippingAddress.postalCode}</div>
              </div>
            ) : (
              <div className="text-gray-500 text-sm">(Not available)</div>
            )}
          </div>
          <OrderStatusStepper
            currentStep={getStepIndex(selectedOrder.status)}
            timeline={selectedOrder.timeline || []}
          />
        </div>
      )}
    </div>
  );
}
